import type { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import { GroundedNpcGait, type NpcStrideClips } from "./GroundedNpcGait";

export type NpcStatePose = "frozen" | "sleep" | "startled";
const BASE = ["idle", "walk", "run"] as const;
const POSES: readonly NpcStatePose[] = ["frozen", "sleep", "startled"];

/** State-pose overlay for authored NPC skins; world movement stays in NPCBehavior. */
export class NpcPoseAnimator {
  private requested: NpcStatePose | null = null;
  private suspended = false;
  private disposed = false;
  private pausedForPose = false;
  private readonly weights: Record<typeof BASE[number], number>;

  static create(groups: readonly AnimationGroup[], scale: number, clips: NpcStrideClips): NpcPoseAnimator | null {
    const gait = GroundedNpcGait.create(groups, scale, clips);
    if (!gait) return null;
    const byName = new Map(groups.map(g => [g.name.replace(/^(Clone of )+/, ""), g]));
    return new NpcPoseAnimator(gait,
      Object.fromEntries(BASE.map(n => [n, byName.get(n)!])) as Record<typeof BASE[number], AnimationGroup>,
      new Map(POSES.filter(n => byName.has(n)).map(n => [n, byName.get(n)!])));
  }

  private constructor(private readonly gait: GroundedNpcGait,
    private readonly base: Record<typeof BASE[number], AnimationGroup>,
    private readonly poses: Map<NpcStatePose, AnimationGroup>) {
    this.weights = { idle: base.idle.weight, walk: base.walk.weight, run: base.run.weight };
    for (const group of poses.values()) { group.start(true, 1); group.weight = 0; group.pause(); }
  }

  setPose(pose: NpcStatePose | null): void {
    this.requested = pose && this.poses.has(pose) ? pose : null;
  }

  update(speed: number, dt: number): void {
    if (this.disposed || this.suspended || dt <= 0) return;
    const inPose = this.requested !== null;
    if (inPose !== this.pausedForPose) {
      this.gait.setSuspended(inPose); this.pausedForPose = inPose;
    }
    // The overlay must not feed its masked weights back into locomotion's
    // exponential blend, or all weights decay during a state transition.
    for (const name of BASE) this.base[name].weight = this.weights[name];
    this.gait.update(speed, dt);
    for (const name of BASE) this.weights[name] = this.base[name].weight;
    const blend = 1 - Math.exp(-dt / .10);
    let poseWeight = 0;
    for (const [name, group] of this.poses) {
      const target = this.requested === name ? 1 : 0;
      if (target && !group.isPlaying) group.play(true);
      group.weight += (target - group.weight) * blend;
      if (!target && group.weight < .00001) { group.weight = 0; group.pause(); }
      poseWeight += group.weight;
    }
    for (const name of BASE) this.base[name].weight = this.weights[name] * Math.max(0, 1 - poseWeight);
  }

  setSuspended(suspended: boolean): void {
    if (this.disposed) return;
    this.suspended = suspended;
    this.gait.setSuspended(suspended || this.requested !== null);
    this.pausedForPose = this.requested !== null;
    if (suspended) for (const group of this.poses.values()) group.pause();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true; this.gait.dispose();
    for (const group of this.poses.values()) group.stop();
  }
}
